/**
 * A workflow's health, in a dot and a few words, the same everywhere: the
 * Workflows list, and the read-only "Used by" panels around the app.
 *
 * The server decides the state (see workflows/organise.rs, the health
 * rule): archived, off, erroring (a failure in 24 hours), never runs (on
 * for 30 days or more without a run), waiting (runs waiting), else fine.
 */
import { Group, Text, Tooltip } from '@mantine/core';
import type { WorkflowHealth, WorkflowHealthState } from '../../api/workflows';
import { TOKENS, formatNumber } from '../../theme';
import { relativeTime } from '../../ui/format';
import type { Vocab } from '../../vocab';

export const HEALTH_LABEL: Record<WorkflowHealthState, string> = {
  ok: 'Running fine',
  erroring: 'Erroring',
  waiting: 'Waiting',
  never_runs: 'Never runs',
  off: 'Off',
  archived: 'Archived',
};

/** Worst first, as the filter offers them. */
export const HEALTH_ORDER: WorkflowHealthState[] = ['erroring', 'never_runs', 'waiting', 'ok', 'off', 'archived'];

export function healthColour(state: WorkflowHealthState): string {
  switch (state) {
    case 'ok': return TOKENS.green;
    case 'erroring': return TOKENS.red;
    case 'waiting': return 'var(--mantine-color-chartBlue-6)';
    case 'never_runs': return 'var(--mantine-color-chartAmber-6)';
    default: return TOKENS.textMuted;
  }
}

/** The words beside the dot: "Last run 3h ago", "2 failures in 24h". */
export function healthText(v: Vocab, h: WorkflowHealth): string {
  switch (h.state) {
    case 'ok': return h.last_run_at ? `Last run ${relativeTime(h.last_run_at)}` : `No ${v.many('run')} yet`;
    case 'erroring': return `${formatNumber(h.failures_24h)} ${h.failures_24h === 1 ? 'failure' : 'failures'} in 24h`;
    case 'waiting': return `${v.count(h.waiting, 'run')} waiting`;
    case 'never_runs': return h.last_run_at ? `No ${v.one('run')} in 30 days` : 'On, never run';
    case 'off': return 'Off';
    case 'archived': return 'Archived';
  }
}

export function HealthDot({ state, size = 8 }: { state: WorkflowHealthState; size?: number }) {
  return (
    <span
      aria-hidden
      data-health={state}
      style={{
        display: 'inline-block', width: size, height: size, borderRadius: '50%', flex: 'none',
        background: state === 'off' || state === 'archived' ? 'transparent' : healthColour(state),
        border: state === 'off' || state === 'archived' ? `1.5px solid ${TOKENS.textMuted}` : undefined,
      }}
    />
  );
}

/** The health column: dot, words, and for an erroring one its last error on hover. */
export function HealthCell({ v, h }: { v: Vocab; h: WorkflowHealth }) {
  const words = healthText(v, h);
  const body = (
    <Group gap={6} wrap="nowrap" data-testid="health" data-state={h.state}>
      <HealthDot state={h.state} />
      <Text size="sm" style={{ color: h.state === 'erroring' ? TOKENS.redText : h.state === 'never_runs' ? TOKENS.amberText : undefined }}>
        {words}
      </Text>
    </Group>
  );
  if (h.state === 'erroring' && h.last_error) {
    return (
      <Tooltip label={h.last_error} multiline maw={360} openDelay={200}>
        <div style={{ display: 'inline-block' }}>{body}</div>
      </Tooltip>
    );
  }
  return body;
}
